// Sharpen Edges — edge-aware sharpening.
// Functional approximation: detects edges using Sobel operator and applies
// sharpening only along detected edges, preserving smooth areas.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface SharpenEdgesParams {
  amount: number; // 0..500, default 100
  radius: number; // 0.5..5, default 1
  threshold: number; // 0..255, default 5
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function gaussianBlur(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): Float32Array {
  const n = w * h;
  const sigma = radius / 3;
  const half = Math.max(1, Math.ceil(3 * sigma));
  const kernelSize = 2 * half + 1;
  const kernel = new Float32Array(kernelSize);
  let sum = 0;
  for (let i = 0; i < kernelSize; i++) {
    const x = i - half;
    const v = Math.exp(-(x * x) / (2 * sigma * sigma));
    kernel[i] = v;
    sum += v;
  }
  for (let i = 0; i < kernelSize; i++) kernel[i] = kernel[i]! / sum;

  const pre = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]! / 255;
    pre[o] = src[o]! * a;
    pre[o + 1] = src[o + 1]! * a;
    pre[o + 2] = src[o + 2]! * a;
    pre[o + 3] = src[o + 3]!;
  }

  const tmp = new Float32Array(n * 4);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernelSize; k++) {
        let sx = x + k - half;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        const o = (row + sx) * 4;
        const wk = kernel[k]!;
        r += pre[o]! * wk;
        g += pre[o + 1]! * wk;
        b += pre[o + 2]! * wk;
        a += pre[o + 3]! * wk;
      }
      const o = (row + x) * 4;
      tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
    }
  }

  const out = new Float32Array(n * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernelSize; k++) {
        let sy = y + k - half;
        if (sy < 0) sy = 0;
        else if (sy >= h) sy = h - 1;
        const o = (sy * w + x) * 4;
        const wk = kernel[k]!;
        r += tmp[o]! * wk;
        g += tmp[o + 1]! * wk;
        b += tmp[o + 2]! * wk;
        a += tmp[o + 3]! * wk;
      }
      const o = (y * w + x) * 4;
      out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
    }
  }

  return out;
}

export const SharpenEdges: Filter<SharpenEdgesParams> = {
  id: "sharpen-edges",
  displayName: "Sharpen Edges",
  defaultParams: {
    amount: 100,
    radius: 1,
    threshold: 5,
  },

  apply(
    input: PixelBuffer,
    params: SharpenEdgesParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const { amount, radius, threshold } = params;

    // Compute blurred version
    const blurred = gaussianBlur(src, w, h, radius);

    // Compute Sobel edge detection
    const edgeMap = new Float32Array(n);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const o = (y * w + x) * 4;

        // Sobel on luminance
        const getLum = (px: number, py: number) => {
          const idx = (py * w + px) * 4;
          return 0.2126 * src[idx]! + 0.7152 * src[idx + 1]! + 0.0722 * src[idx + 2]!;
        };

        const gx =
          -getLum(x - 1, y - 1) + getLum(x + 1, y - 1) +
          -2 * getLum(x - 1, y) + 2 * getLum(x + 1, y) +
          -getLum(x - 1, y + 1) + getLum(x + 1, y + 1);

        const gy =
          -getLum(x - 1, y - 1) - 2 * getLum(x, y - 1) - getLum(x + 1, y - 1) +
          getLum(x - 1, y + 1) + 2 * getLum(x, y + 1) + getLum(x + 1, y + 1);

        edgeMap[y * w + x] = Math.sqrt(gx * gx + gy * gy);
      }
    }

    // Apply sharpening only where edges exceed threshold
    const amountFactor = amount / 100;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const edge = edgeMap[i]!;

      if (edge > threshold) {
        // Edge strength as weight (0 to 1)
        const weight = Math.min(1, (edge - threshold) / (128 - threshold));

        // Unsharp mask: original + amount * (original - blurred)
        for (let c = 0; c < 3; c++) {
          const orig = src[o + c]!;
          const blur = blurred[o + c]!;
          const diff = orig - blur;
          const sharpened = orig + amountFactor * diff * weight;
          dst[o + c] = clamp8(Math.round(sharpened));
        }
      } else {
        dst[o] = src[o]!;
        dst[o + 1] = src[o + 1]!;
        dst[o + 2] = src[o + 2]!;
      }
      dst[o + 3] = src[o + 3]!;
    }

    return out;
  },
};
